import React, { useCallback, useMemo } from "react";
var Quadrant;
(function (Quadrant) {
    Quadrant[Quadrant["TOP_RIGHT"] = 1] = "TOP_RIGHT";
    Quadrant[Quadrant["TOP_LEFT"] = 2] = "TOP_LEFT";
    Quadrant[Quadrant["BOTTOM_LEFT"] = 3] = "BOTTOM_LEFT";
    Quadrant[Quadrant["BOTTOM_RIGHT"] = 4] = "BOTTOM_RIGHT";
    Quadrant[Quadrant["MID_TOP_LEFT"] = 12] = "MID_TOP_LEFT";
    Quadrant[Quadrant["MID_TOP_RIGHT"] = 21] = "MID_TOP_RIGHT";
    Quadrant[Quadrant["MID_LEFT_TOP"] = 23] = "MID_LEFT_TOP";
    Quadrant[Quadrant["MID_LEFT_BOTTOM"] = 32] = "MID_LEFT_BOTTOM";
    Quadrant[Quadrant["MID_BOTTOM_LEFT"] = 34] = "MID_BOTTOM_LEFT";
    Quadrant[Quadrant["MID_BOTTOM_RIGHT"] = 43] = "MID_BOTTOM_RIGHT";
    Quadrant[Quadrant["MID_RIGHT_TOP"] = 14] = "MID_RIGHT_TOP";
    Quadrant[Quadrant["MID_RIGHT_BOTTOM"] = 41] = "MID_RIGHT_BOTTOM";
    Quadrant[Quadrant["MIDDLE"] = 1234] = "MIDDLE";
})(Quadrant || (Quadrant = {}));
export const Ellipse = ({ xScale, yScale, radius, radiusX, radiusY, centerX, centerY }) => {
    const xDomain = xScale.domain();
    const yDomain = yScale.domain();
    const style = { fill: "none", stroke: "#CCC", strokeDasharray: "4" };
    const getQuadrant = useCallback(getQuadrantFunction, []);
    const getEllipseSettings = useCallback(getEllipseSettingsFunction, []);
    const isCenterInFrame = useMemo(() => checkCenterInFrame(xScale, yScale), [
        xScale,
        yScale
    ]);
    const crossingPoints = getCrossingPoints(xDomain, yDomain, radius);
    const ellipseSettings = getEllipseSettings(getQuadrant(xDomain, yDomain), crossingPoints, xScale, yScale);
    return isCenterInFrame ? (React.createElement("ellipse", { cx: centerX, cy: centerY, rx: radiusX, ry: radiusY, style: style })) : ellipseSettings ? (React.createElement("path", { style: style, d: ` M ${ellipseSettings.origin.x} ${ellipseSettings.origin.y} A ${radiusX} ${radiusY} 0 0 1 ${ellipseSettings.destination.x} ${ellipseSettings.destination.y}` })) : null;
};
export const getCrossingPoints = (xDomain, yDomain, radius) => {
    const top = getX(yDomain[1], radius, radius, 0, 0)
        .filter(value => isInside(value, xDomain))
        .map(value => [value, yDomain[1]]);
    const bottom = getX(yDomain[0], radius, radius, 0, 0)
        .filter(value => isInside(value, xDomain))
        .map(value => [value, yDomain[0]]);
    const right = getY(xDomain[1], radius, radius, 0, 0)
        .filter(value => isInside(value, yDomain))
        .map(value => [xDomain[1], value]);
    const left = getY(xDomain[0], radius, radius, 0, 0)
        .filter(value => isInside(value, yDomain))
        .map(value => [xDomain[0], value]);
    return { left, top, right, bottom };
};
const getX = (y, radiusX, radiusY, centerX, centerY) => {
    const solution = realSquareRoots((1 - Math.pow(y - centerY, 2) / Math.pow(radiusY, 2)) * Math.pow(radiusX, 2)).map(result => result + centerX);
    return solution;
};
const getY = (x, radiusX, radiusY, centerX, centerY) => {
    const solution = realSquareRoots((1 - Math.pow(x - centerX, 2) / Math.pow(radiusX, 2)) * Math.pow(radiusY, 2)).map(result => result + centerY);
    return solution;
};
const realSquareRoots = (number) => number >= 0 ? [Math.sqrt(number), -Math.sqrt(number)] : [];
const isInside = (value, range) => value > range[0] && value < range[1];
const getEllipseSettingsFunction = (quadrant, crossingPoints, xScale, yScale) => {
    let points = [];
    switch (quadrant) {
        case Quadrant.TOP_RIGHT:
            points = removeEmptyArrays([
                ...crossingPoints.left,
                ...crossingPoints.top,
                ...crossingPoints.right,
                ...crossingPoints.bottom
            ]);
            break;
        case Quadrant.TOP_LEFT:
            points = removeEmptyArrays([
                ...crossingPoints.bottom,
                ...crossingPoints.left,
                ...crossingPoints.top,
                ...crossingPoints.right
            ]);
            break;
        case Quadrant.BOTTOM_LEFT:
            points = removeEmptyArrays([
                ...crossingPoints.right,
                ...crossingPoints.bottom,
                ...crossingPoints.left,
                ...crossingPoints.top
            ]);
            break;
        case Quadrant.BOTTOM_RIGHT:
            points = removeEmptyArrays([
                ...crossingPoints.top,
                ...crossingPoints.right,
                ...crossingPoints.bottom,
                ...crossingPoints.left
            ]);
            break;
        case Quadrant.MID_TOP_LEFT: {
            const topPoints = crossingPoints.top && crossingPoints.top.length > 1
                ? []
                : crossingPoints.top;
            points = removeEmptyArrays([
                ...crossingPoints.bottom,
                ...crossingPoints.left,
                ...topPoints,
                ...crossingPoints.right
            ]);
            break;
        }
        case Quadrant.MID_TOP_RIGHT: {
            const topPoints = crossingPoints.top && crossingPoints.top.length > 1
                ? []
                : crossingPoints.top;
            points = removeEmptyArrays([
                ...crossingPoints.left,
                ...topPoints,
                ...crossingPoints.right,
                ...crossingPoints.bottom
            ]);
            break;
        }
        case Quadrant.MID_RIGHT_TOP: {
            const rightPoints = crossingPoints.right && crossingPoints.right.length > 1
                ? []
                : crossingPoints.right;
            points = removeEmptyArrays([
                ...crossingPoints.left,
                ...crossingPoints.top,
                ...rightPoints,
                ...crossingPoints.bottom
            ]);
            break;
        }
        case Quadrant.MID_RIGHT_BOTTOM: {
            const rightPoints = crossingPoints.right && crossingPoints.right.length > 1
                ? []
                : crossingPoints.right;
            points = removeEmptyArrays([
                ...crossingPoints.top,
                ...rightPoints,
                ...crossingPoints.bottom,
                ...crossingPoints.left
            ]);
            break;
        }
        case Quadrant.MID_BOTTOM_LEFT: {
            const bottomPoints = crossingPoints.bottom && crossingPoints.bottom.length > 1
                ? []
                : crossingPoints.bottom;
            points = removeEmptyArrays([
                ...crossingPoints.right,
                ...bottomPoints,
                ...crossingPoints.left,
                ...crossingPoints.top
            ]);
            break;
        }
        case Quadrant.MID_BOTTOM_RIGHT: {
            const bottomPoints = crossingPoints.bottom && crossingPoints.bottom.length > 1
                ? []
                : crossingPoints.bottom;
            points = removeEmptyArrays([
                ...crossingPoints.top,
                ...crossingPoints.right,
                ...bottomPoints,
                ...crossingPoints.left
            ]);
            break;
        }
        case Quadrant.MID_LEFT_TOP: {
            const leftPoints = crossingPoints.left && crossingPoints.left.length > 1
                ? []
                : crossingPoints.left;
            points = removeEmptyArrays([
                ...crossingPoints.bottom,
                ...leftPoints,
                ...crossingPoints.top,
                ...crossingPoints.right
            ]);
            break;
        }
        case Quadrant.MID_LEFT_BOTTOM: {
            const leftPoints = crossingPoints.left && crossingPoints.left.length > 1
                ? []
                : crossingPoints.left;
            points = removeEmptyArrays([
                ...crossingPoints.right,
                ...crossingPoints.bottom,
                ...leftPoints,
                ...crossingPoints.top
            ]);
            break;
        }
        default:
            break;
    }
    return points.length > 0 && points.length === 2
        ? {
            origin: {
                x: xScale(points[0][0]),
                y: yScale(points[0][1])
            },
            destination: {
                x: xScale(points[1][0]),
                y: yScale(points[1][1])
            }
        }
        : null;
};
const removeEmptyArrays = array => array.filter(item => item);
const checkCenterInFrame = (xScale, yScale) => {
    const xDomain = xScale.domain();
    const yDomain = yScale.domain();
    return xDomain[0] < 0 && xDomain[1] > 0 && yDomain[0] < 0 && yDomain[1] > 0;
};
const getQuadrantFunction = (xDomain, yDomain) => {
    if (xDomain[0] > 0) {
        if (yDomain[0] > 0) {
            return Quadrant.TOP_RIGHT;
        }
        else if (yDomain[1] < 0) {
            return Quadrant.BOTTOM_RIGHT;
        }
        else {
            return 0 - yDomain[0] > yDomain[1]
                ? Quadrant.MID_RIGHT_BOTTOM
                : Quadrant.MID_RIGHT_TOP;
        }
    }
    else if (xDomain[1] < 0) {
        if (yDomain[0] > 0) {
            return Quadrant.TOP_LEFT;
        }
        else if (yDomain[1] < 0) {
            return Quadrant.MID_BOTTOM_LEFT;
        }
        else {
            return 0 - yDomain[0] > yDomain[1]
                ? Quadrant.MID_LEFT_BOTTOM
                : Quadrant.MID_LEFT_TOP;
        }
    }
    else {
        if (yDomain[0] > 0) {
            return 0 - xDomain[0] > xDomain[1]
                ? Quadrant.MID_TOP_LEFT
                : Quadrant.MID_TOP_RIGHT;
        }
        else if (yDomain[1] < 0) {
            return 0 - xDomain[0] > xDomain[1]
                ? Quadrant.MID_BOTTOM_LEFT
                : Quadrant.MID_BOTTOM_RIGHT;
        }
        else {
            return 2134;
        }
    }
};
//# sourceMappingURL=Ellipse.js.map